<template>
  <div class="wizard-map">
    <!-- Back button -->
    <button
      v-if="previousStep"
      v-tooltip="`To ${previousStep.label}`"
      class="wizard-map__back icon hoverable has-text-primary"
      @click="$emit('step-back')"
    >
      <i class="fa fa-step-backward" />
    </button>

    <!-- Steps track (grid) -->
    <div
      class="wizard-map__track"
      :style="{ 'grid-template-columns': trackColumns }"
    >
      <!--
        Grid layout (1-based columns):
          col 1     col 2     col 3     col 4     col 5
          [node-0] [gap-0-1] [node-1] [gap-1-2] [node-2]
        Connectors span 3 columns (prev-node → gap → next-node)
        and sit behind nodes via z-index.
      -->
      <template v-for="(step, index) in steps" :key="`step-${index}`">
        <!-- Connector: spans from prev node col through next node col -->
        <div
          v-if="index > 0 && index < steps.length"
          class="wizard-map__connector"
          :class="{
            'is-active': isBeforeCurrentStep(index),
            'is-loading': isLoadingCurrentStep(index),
          }"
          :style="connectorStyle(index)"
        >
          <div class="wizard-map__connector-fill" />
        </div>

        <!-- Node: placed in its own column -->
        <a
          class="wizard-map__node"
          :class="{
            'is-active':
              isBeforeCurrentStep(index) && !isLoadingCurrentStep(index),
            'is-current': index === currentStepIndex,
          }"
          :style="nodeStyle(index)"
          @click="
            isBeforeCurrentStep(index) &&
              !isLoadingCurrentStep(index) &&
              $emit('step-to', step.name)
          "
        >
          <span v-if="isLoadingCurrentStep(index)" class="icon">
            <i class="fa fa-spinner fa-spin" />
          </span>
          <span v-else class="icon">
            <i :class="step.iconClasses" />
          </span>
          <span
            v-if="step.hasWarning"
            v-tooltip="step.message"
            class="wizard-map__badge"
          >
            <i class="fa fa-exclamation-triangle" />
          </span>
        </a>
      </template>

      <!-- Row 2: labels placed explicitly under their node column -->
      <span
        v-for="(step, index) in steps"
        :key="`label-${index}`"
        class="wizard-map__label"
        :class="{
          'is-active':
            isBeforeCurrentStep(index) && !isLoadingCurrentStep(index),
          'is-current': index === currentStepIndex,
        }"
        :style="labelStyle(index)"
        @click="
          isBeforeCurrentStep(index) &&
            !isLoadingCurrentStep(index) &&
            $emit('step-to', step.name)
        "
      >
        {{ step.label }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WizardMap',
  props: {
    steps: {
      type: Array,
      default: () => [],
    },
    currentStepIndex: {
      type: Number,
      default: 0,
    },
    previousStep: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['step-back', 'step-to'],
  computed: {
    trackColumns() {
      // Fixed node-width columns so labels don't widen them and expose connectors.
      const node = 'var(--wiz-node-size)'
      return this.steps
        .map((_, i) => (i === 0 ? node : `1fr ${node}`))
        .join(' ')
    },
  },
  methods: {
    // Grid column for node at step index (1-based): 2*index + 1
    nodeStyle(index) {
      const col = 2 * index + 1
      return { gridRow: 1, gridColumn: col }
    },
    // Connector between step (index-1) and step (index)
    // Spans 3 columns: prev node col → gap col → this node col
    connectorStyle(index) {
      const startCol = 2 * (index - 1) + 1 // prev node column
      const endCol = 2 * index + 1 + 1 // after this node column (exclusive)
      return { gridRow: 1, gridColumn: `${startCol} / ${endCol}` }
    },
    // Label sits under its node column, row 2
    labelStyle(index) {
      const col = 2 * index + 1
      return { gridRow: 2, gridColumn: col }
    },
    isBeforeCurrentStep(stepIndex) {
      const lastStepIndex = this.steps.length
      const currentStepIsUndefined =
        this.currentStepIndex === -1 && stepIndex === lastStepIndex
      return currentStepIsUndefined || stepIndex <= this.currentStepIndex
    },
    isLoadingCurrentStep(stepIndex) {
      return this.loading && stepIndex === this.currentStepIndex
    },
  },
}
</script>

<style lang="scss" scoped>
/* ── Component tokens ── */
.wizard-map {
  --wiz-node-size: var(--space-12);
  --wiz-connector-height: var(--space-3);
  --wiz-label-size: var(--text-sm);
  --wiz-padding-block: var(--space-4);
  --wiz-padding-inline: var(--space-12);

  @include mobile {
    --wiz-node-size: var(--space-8);
    --wiz-connector-height: var(--space-2);
    --wiz-label-size: var(--text-2xs);
    --wiz-padding-block: var(--space-2);
    --wiz-padding-inline: var(--space-3);
  }
}

/* ── Root layout ── */
.wizard-map {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--wiz-padding-block) var(--wiz-padding-inline);
  width: 100%;
  overflow: hidden;

  @include mobile {
    gap: var(--space-2);
  }
}

/* ── Back button ── */
.wizard-map__back {
  all: unset;
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
  align-self: start;
  padding-top: calc(var(--wiz-node-size) / 2 - 0.5em);
}

/* ── Grid track ── */
.wizard-map__track {
  display: grid;
  /* rows & columns set via inline style */
  align-items: center;
  flex: 1;
  min-width: 0;
}

/* ── Nodes ── */
.wizard-map__node {
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: center;
  width: var(--wiz-node-size);
  height: var(--wiz-node-size);
  border-radius: var(--radius-full);
  background-color: var(--gray-200);
  color: var(--gray-400);
  cursor: not-allowed;
  transition: all var(--transition-fast);
  flex-shrink: 0;
  position: relative;
  z-index: 2;

  &.is-active {
    cursor: pointer;
    background-color: var(--brand);
    color: var(--text-on-brand);

    &:hover {
      transform: scale(1.05);
    }
  }

  &.is-current {
    box-shadow: 0 0 0 3px var(--ring-focus);
  }
}

/* ── Connectors ── */
.wizard-map__connector {
  /* Spans 3 grid columns (prev node → gap → next node) via inline style */
  height: var(--wiz-connector-height);
  background-color: var(--gray-200);
  border-radius: var(--radius-full);
  overflow: hidden;
  z-index: 1;
}

.wizard-map__connector-fill {
  height: 100%;
  width: 0;
  border-radius: var(--radius-full);
  transition: width var(--transition-normal);

  .is-active > & {
    width: 100%;
    background-color: var(--brand);
  }

  .is-loading > & {
    width: 40%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--brand) 50%,
      transparent 100%
    );
    animation: shimmer 1.2s ease-in-out infinite;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}

/* ── Labels ── */
.wizard-map__label {
  font-size: var(--wiz-label-size);
  font-weight: var(--font-semibold);
  color: var(--gray-400);
  text-align: center;
  justify-self: center;
  white-space: nowrap;
  padding-top: var(--space-1);

  @include mobile {
    white-space: normal;
    word-break: normal;
  }
  cursor: not-allowed;
  pointer-events: none;

  &.is-active {
    cursor: pointer;
    color: var(--brand);
    pointer-events: auto;
  }

  &.is-current {
    color: var(--brand);
    font-weight: var(--font-bold);
  }
}

/* ── Warning badge ── */
.wizard-map__badge {
  position: absolute;
  top: calc(var(--space-1) * -1);
  right: calc(var(--space-1) * -1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--space-5);
  height: var(--space-5);
  border-radius: var(--radius-full);
  background-color: var(--warning);
  color: var(--white);
  font-size: var(--text-2xs);
}

/* ── Very small screens: show only current step ── */
@media screen and (max-width: 358px) {
  .wizard-map__track {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2);
  }

  .wizard-map__connector {
    display: none;
  }

  .wizard-map__node:not(.is-current),
  .wizard-map__label:not(.is-current) {
    display: none;
  }

  .wizard-map__label {
    padding-top: 0;

    &.is-offset {
      padding-top: 0;
    }
  }
}
</style>
